import { useEffect } from 'react';
import { Box } from '@mui/material';
import { loadUsersWithTenant } from 'src/redux/users/actions';
import { useDispatch, useSelector } from 'react-redux';

import { useTranslation } from 'react-i18next';
import { FilterSelect, H1, TableAdvancedMetaColumn, Toolbar, usersColumnVisibilityModel, UsersTable } from 'src/components';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { PermissionEnum } from 'src/shared/enums/permission.enum';
import config from 'src/config';
import { selectUsers } from 'src/redux/users/selectors';
import { UserType } from 'src/types';
import emptyUsers from 'src/assets/emptyUsers.svg';
import { useQueryParam } from 'src/hooks';
import { useNavigate } from 'react-router-dom';
import { setParams } from 'src/utils';
import { PATHS } from 'src/navigation';
import { useTenants } from 'src/api/tenant';
import { useTableFilters } from 'src/components/Table/hooks';
import { DashboardWrapper } from '../wrappers';

function TenantUsersContainer() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [userId, setUserId] = useQueryParam('id', '');
  const users = useSelector(selectUsers);
  const { tenants } = useTenants();
  const {
    data: filteredUsers,
    filters: usersFilters,
    isFilters: isUsersFilters,
    onChangeFilters: onChangeUsersFilters,
    resetFilters: resetUsersFilters,
  } = useTableFilters<UserType>({ items: users });

  const usersTotal = users.length;
  const canViewUserProfile = config.can([PermissionEnum.VIEW_FULL_USER_PROFILE]) || config.can([PermissionEnum.VIEW_FIELD_USER_PROFILE]);

  const columnsVisibility = {
    ...usersColumnVisibilityModel,
    firstName: true,
    lastName: true,
    email: true,
    registrationDate: true,
    clearStatus: true,
    userRoles: true,
    verificationStatus: true,
    status: true,
  };

  const navigateToUserProfile = (id: string) => {
    setUserId(id);
    navigate(setParams(PATHS.USER_PROFILE_PAGE, { uuid: id }));
  };

  const clearUserId = () => {
    setUserId('');
  };

  const goTo = (params: GridRenderCellParams) => {
    navigateToUserProfile(params.row.uuid);
  };

  const hiddenFromToggleFields = ['favorite'];

  const getTogglableColumns = (columns: any) => {
    return columns.filter((column: any) => !hiddenFromToggleFields.includes(column.field)).map((column: any) => column.field);
  };

  const metaColumns: TableAdvancedMetaColumn[] = [
    {
      index: 3,
      column: {
        field: 'tenantId',
        headerName: t('users.tenant'),
        sortable: false,
        valueGetter: (params: GridRenderCellParams) => params.row.tenantId,
        renderCell: (params) => tenants?.find((tenant) => tenant?.id === params.row?.tenantId)?.title || '',
        renderHeader: () => (
          <FilterSelect
            name="tenantId"
            title={t('users.tenant')}
            options={tenants.map((tenant) => ({
              title: tenant.title,
              value: tenant.id,
            }))}
            filters={usersFilters}
            onChange={onChangeUsersFilters}
          />
        ),
        width: 160,
      },
    },
  ];

  useEffect(() => {
    dispatch(loadUsersWithTenant.init());
  }, []);

  return (
    <DashboardWrapper
      breadcrumbs={[[t('sidebarItems.tenantUsers'), usersTotal ? ` (${usersTotal})` : '']]}
      navigationChildren={users?.length ? <Toolbar options="mini" /> : null}
    >
      <Box>
        {users?.length ? (
          <Box component="main" sx={{ height: 'calc(100vh - 150px)', pt: 4 }}>
            <UsersTable
              statePersistId="tenant_users_table"
              users={filteredUsers}
              metaColumns={metaColumns}
              columnVisibilityModel={columnsVisibility}
              toolbarProps={{ options: 'mini' }}
              goTo={canViewUserProfile ? goTo : undefined}
              onCellClick={(params) =>
                params.field === 'firstName' || params.field === 'lastName' ? navigateToUserProfile(params.row.uuid) : undefined
              }
              slotProps={{ columnsPanel: { getTogglableColumns } }}
              disableVirtualization
              usePaginaionParams
              idToScroll={userId}
              onScroll={clearUserId}
              isFilters={isUsersFilters}
              resetFilters={resetUsersFilters}
            />
          </Box>
        ) : (
          <Box
            sx={{
              mt: 14,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              flexDirection: 'column',
            }}
          >
            <Box sx={{ ml: 1 }}>
              <img src={emptyUsers} alt="Empty list" />
            </Box>
            <H1 sx={{ mb: 3 }}>{t('usersPage.noUsers')}</H1>
          </Box>
        )}
      </Box>
    </DashboardWrapper>
  );
}

export default TenantUsersContainer;
